import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { ResponseError } from 'services/api/base';
import handleAtuthorizationErrors from 'helper/tokenError';
import sendErrorToSentry from 'helper/sendErrorToSentry';

/** Tipo de erro que a tela mostra no lugar (bloco, faixa ou aviso), sem sair da página. */
export type StudiesErrorKind = 'network' | 'server' | 'forbidden';

/**
 * `primary`: a consulta da lista. Um 403 de permissão mantém o comportamento atual (/forbidden).
 * `auxiliary`: consultas de apoio (opções, cenários, status). Um 403 de permissão fica na tela.
 */
export type StudiesErrorMode = 'primary' | 'auxiliary';

export interface StudiesErrorOptions {
  mode: StudiesErrorMode;
}

const JWT_INVALID_MESSAGE = 'JWT Inválido';

// Falha de transporte: a requisição não teve resposta legível (o baseQuery já repetiu 2×).
const NETWORK_STATUSES = ['FETCH_ERROR', 'PARSING_ERROR', 'TIMEOUT_ERROR'];

// O baseQuery entrega ResponseError { status, data }. Um SerializedError (exceção dentro de
// queryFn/transformResponse) não tem `status`: vira CUSTOM_ERROR, para o Sentry ter o que
// mostrar e a tela tratar como erro do servidor.
function toResponseError(error: unknown): ResponseError | null {
  if (!error || typeof error !== 'object') return null;

  const candidate = error as Partial<ResponseError> & { message?: string };
  if (candidate.status === undefined || candidate.status === null) {
    return { status: 'CUSTOM_ERROR', data: candidate.message ?? 'Erro inesperado' };
  }

  return candidate as ResponseError;
}

const isJwtInvalid = (error: ResponseError) => (
  error.status === 403 && error.data === JWT_INVALID_MESSAGE
);

const isForbidden = (error: ResponseError) => error.status === 403 && !isJwtInvalid(error);

/**
 * Classifica o erro sem efeitos colaterais.
 *
 * @param error - Erro da consulta, como sai do baseQuery.
 * @param mode - Papel da consulta na tela.
 * @returns O tipo de erro local, ou null quando não há erro local a mostrar (sem erro, sessão
 *   expirada ou 403 da lista, que saem da tela).
 */
export function classifyStudiesError(
  error: ResponseError | null,
  mode: StudiesErrorMode,
): StudiesErrorKind | null {
  if (!error || isJwtInvalid(error)) return null;
  if (isForbidden(error)) return mode === 'auxiliary' ? 'forbidden' : null;
  if (NETWORK_STATUSES.includes(String(error.status))) return 'network';

  return 'server';
}

/**
 * Erros das consultas da aba Meus Estudos (Flux Personalizado) tratados na própria tela.
 *
 * Diferente do `useHandleRequestErrors`, NUNCA navega para /error nem zera o cache do RTK
 * Query: falha de rede ou de servidor mantém a lista e os filtros. Só a sessão expirada
 * (403 "JWT Inválido", logout) e o 403 de permissão da lista (`primary`, /forbidden) saem
 * da página, como hoje.
 *
 * O Sentry recebe um evento por sequência de falhas: as repetições (polling, "Tentar de novo")
 * não geram eventos novos até a consulta voltar a dar certo (`error` vazio).
 *
 * `mode` é fixo por chamada: cada consulta usa sempre o mesmo modo. Os efeitos são deduplicados
 * pela identidade do erro, então trocar o modo com o mesmo erro não repete navegação nem Sentry.
 *
 * @param error - `error` do hook do RTK Query.
 * @param options - `mode` da consulta (ver {@link StudiesErrorMode}).
 * @returns `'network' | 'server' | 'forbidden'` para mostrar na tela, ou null.
 */
export default function useStudiesErrorState(
  error: unknown,
  { mode }: StudiesErrorOptions,
): StudiesErrorKind | null {
  const navigate = useNavigate();
  const responseError = useMemo(() => toResponseError(error), [error]);
  const handledErrorRef = useRef<ResponseError | null>(null);
  const reportedInSequenceRef = useRef(false);

  useEffect(() => {
    if (!responseError) {
      handledErrorRef.current = null;
      reportedInSequenceRef.current = false;
      return;
    }

    // O mesmo erro re-renderizando (ou o navigate mudando de identidade) não repete efeitos
    if (handledErrorRef.current === responseError) return;
    handledErrorRef.current = responseError;

    if (isJwtInvalid(responseError) || (isForbidden(responseError) && mode === 'primary')) {
      handleAtuthorizationErrors(responseError, navigate);
      return;
    }

    // 403 de permissão não é bug; o handleApiError também não o envia
    if (isForbidden(responseError)) return;

    if (!reportedInSequenceRef.current) {
      reportedInSequenceRef.current = true;
      sendErrorToSentry(responseError);
    }
  }, [responseError, mode, navigate]);

  return useMemo(() => classifyStudiesError(responseError, mode), [responseError, mode]);
}
